import React, {useState, useEffect} from "react";
import Header from "../../components/Private/Header";
import {Spinner} from "@heroui/react";
import {ReactComponent as Search} from "../../assets/icons/search (2).svg";
import {useMutation, useQuery} from "@tanstack/react-query";
import {request} from "../../lib/request";
import {toastError, toastSuccess} from "../../lib/toast";
import {Link, useSearchParams} from "react-router";

const isAvailable = (value) => value === 1 || value === true || value === "1";

const formatPrice = (value) => Number(value || 0).toLocaleString("fa-IR");

const getProductPricing = (product) => {
    const options = Array.isArray(product?.options) ? product.options : [];

    if (options.length === 0) {
        return {salePrice: 0, basePrice: 0, discountPercent: null};
    }

    if (product?.discount_percent) {
        const pricedOption = options.find((option) => Number(option.discount_percent) === Number(product.discount_percent))
            || options[0];

        return {
            salePrice: Number(pricedOption.price_discount) || 0,
            basePrice: Number(pricedOption.price) || 0,
            discountPercent: Number(product.discount_percent),
        };
    }

  return {
        salePrice: Number(options[0].price_discount) || 0,
        basePrice: Number(options[0].price) || 0,
        discountPercent: null,
    };
};

const PriceDisplay = ({salePrice, basePrice, discountPercent}) => (
    <div className="rest-menu-card__price">
        {discountPercent ? (
            <>
                <span className="rest-menu-card__price-old">{formatPrice(basePrice)}</span>
                <span>{formatPrice(salePrice)}</span>
                <span className="rest-menu-card__discount-badge">{discountPercent.toLocaleString("fa-IR")}٪</span>
            </>
        ) : (
            <span>{formatPrice(salePrice)}</span>
        )}
        <span className="rest-menu-card__price-unit">تومان</span>
    </div>
);

const AvailabilityToggle = ({available, loading, compact, onClick}) => (
    <button
        type="button"
        onClick={onClick}
        disabled={loading}
        className={`rest-menu-avail ${compact ? "rest-menu-avail--compact" : ""} ${
            available ? "rest-menu-avail--available" : "rest-menu-avail--unavailable"
        }`}
    >
        {loading ? (
            <Spinner size="sm" color={available ? "success" : "danger"} variant="dots"/>
        ) : (
            <>
                <span className="rest-menu-avail__dot" aria-hidden="true"/>
                {available ? "موجود" : "ناموجود"}
            </>
        )}
    </button>
);

const ProductsMenu = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const urlCategory = searchParams.get("category_id");
    const urlSearch = searchParams.get("search");

    const [category_id, setCategory_id] = useState(urlCategory ? parseInt(urlCategory, 10) : "");
    const [search, setSearch] = useState(urlSearch || "");
    const [pendingKey, setPendingKey] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        if (category_id) params.set("category_id", category_id);

        const current = searchParams.toString();
        const next = params.toString();
        if (current !== next) {
            setSearchParams(params, {replace: true});
        }
    }, [search, category_id, searchParams, setSearchParams]);


    const {data, isLoading, isError, error, refetch} = useQuery({
        queryKey: ["products", "index_food", category_id || null, search || null],
        queryFn: async () =>
            await request({
                method: "get",
                url: `/restaurant/products/index_food`,
                query: {
                    category_id: category_id || undefined,
                    search: search || undefined,
                },
            }),
        staleTime: 1000 * 30,
        retry: 1,
    });

    const {data: select, refetch: refetchCategories} = useQuery({
        queryKey: ["select", "item"],
        queryFn: async () =>
            await request({
                method: "get",
                url: `/restaurant/products/categories`,
            }),
        staleTime: 1000 * 60 * 5,
    });

    const {mutate} = useMutation({
        mutationFn: async (payload) =>
            await request({
                url: `/restaurant/products/change_status`,
                method: "post",
                data: payload,
            }),
        onMutate: ({id, type}) => {
            setPendingKey(`${type}-${id}`);
        },
        onSuccess: () => {
            toastSuccess("وضعیت با موفقیت تغییر کرد");
            refetch();
            refetchCategories();
        },
        onError: () => toastError("تغییر وضعیت انجام نشد."),
        onSettled: () => setPendingKey(null),
    });

    const handleChangeStatus = (id, type) => {
        mutate({id, type});
    };

    const products = data?.data || [];
    const selectedCategory = select?.data?.find((i) => i.id === category_id);
    const filterQuery = searchParams.toString();
    const isPending = (id, type) => pendingKey === `${type}-${id}`;

    return (
        <>
            <Header title="منو محصولات"/>

            <section className="rest-menu-page">
                <div className="rest-menu-toolbar">
                    <div className="rest-menu-toolbar__row">
                        <div className="rest-menu-field">
                            <label className="rest-menu-field__label" htmlFor="menu-category">
                                دسته‌بندی
                            </label>
                            <select
                                id="menu-category"
                                className="rest-menu-select"
                                value={category_id || ""}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setCategory_id(value ? parseInt(value, 10) : "");
                                }}
                            >
                                <option value="">همه دسته‌ها</option>
                                {select?.data?.map((item) => (
                                    <option key={item.id} value={item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="rest-menu-field">
                            <label className="rest-menu-field__label" htmlFor="menu-search">
                                جستجو
                            </label>
                            <div className="rest-menu-search">
                                <Search className="rest-menu-search__icon" aria-hidden="true"/>
                                <input
                                    id="menu-search"
                                    type="search"
                                    className="rest-menu-search__input"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="نام محصول را بنویسید..."
                                    autoComplete="off"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="rest-menu-toolbar__footer">
                        {!isLoading && products.length > 0 && (
                            <p className="rest-menu-toolbar__count">
                                {products.length.toLocaleString("fa-IR")} محصول
                            </p>
                        )}

                        {category_id && selectedCategory && (
                            <div className="rest-menu-toolbar__category-status">
                                <span className="rest-menu-toolbar__hint">
                                    وضعیت «{selectedCategory.name}»
                                </span>
                                <AvailabilityToggle
                                    available={isAvailable(selectedCategory.is_available)}
                                    loading={isPending(category_id, "category")}
                                    onClick={() => handleChangeStatus(category_id, "category")}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="rest-menu-content">
                    {isLoading ? (
                        <div className="rest-orders-state rest-orders-state--loading">
                            <Spinner color="warning"/>
                            <p>در حال بارگذاری منو...</p>
                        </div>
                    ) : isError ? (
                        <div className="rest-orders-state rest-orders-state--empty">
                            <p className="rest-orders-state__title">بارگذاری منو ناموفق بود</p>
                            <p className="rest-orders-state__text">
                                {error?.code === 'ECONNABORTED'
                                    ? 'زمان پاسخ سرور بیش از حد طول کشید. دوباره تلاش کنید.'
                                    : 'خطا در دریافت لیست محصولات. اتصال اینترنت یا سرور را بررسی کنید.'}
                            </p>
                            <button
                                type="button"
                                onClick={() => refetch()}
                                className="rest-menu-avail rest-menu-avail--available mt-2"
                            >
                                تلاش مجدد
                            </button>
                        </div>
                    ) : products.length === 0 ? (
                        <div className="rest-orders-state rest-orders-state--empty">
                            <img
                                src="/images/Empty page.png"
                                alt=""
                                className="rest-orders-state__image"
                            />
                            <p className="rest-orders-state__title">محصولی یافت نشد</p>
                            <p className="rest-orders-state__text">
                                {search || category_id
                                    ? "با این فیلترها محصولی وجود ندارد. فیلترها را تغییر دهید."
                                    : "هنوز محصولی در منو ثبت نشده است."}
                            </p>
                        </div>
                    ) : (
                        <div className="rest-menu-list">
                                {products.map((product) => {
                                    const hasVariants =
                                        Array.isArray(product?.options) && product.options.length > 1;
                                    const pricing = getProductPricing(product);
                                    const isPinnedUnavailable = !isAvailable(product.is_available)
                                        || (hasVariants && product.options.some((item) => !isAvailable(item.is_available)));
                                    const editUrl = `/products-menu/${product.id}${
                                        filterQuery ? `?${filterQuery}` : ""
                                    }`;

                                    return (
                                        <article
                                            key={product.id}
                                            className={`rest-menu-card${isPinnedUnavailable ? " rest-menu-card--pinned-unavailable" : ""}`}
                                        >
                                            <div className="rest-menu-card__main">
                                                <div className="rest-menu-card__info">
                                                    <Link to={editUrl} className="rest-menu-card__link">
                                                        <div className="rest-menu-card__meta">
                                                            {product.category?.name && (
                                                                <span className="rest-menu-card__category">
                                                                    {product.category.name}
                                                                </span>
                                                            )}
                                                            {product.has_discount && (
                                                                <span className="rest-menu-card__discount-flag">تخفیف‌دار</span>
                                                            )}
                                                            <span className="rest-menu-card__edit-hint">
                                                                ویرایش و تخفیف
                                                            </span>
                                                        </div>
                                                        <h2 className="rest-menu-card__title">{product.name}</h2>
                                                        {product.description && (
                                                            <p className="rest-menu-card__desc">
                                                                {product.description}
                                                            </p>
                                                        )}
                                                    </Link>
                                                </div>

                                                {!hasVariants && (
                                                    <div className="rest-menu-card__aside">
                                                        <PriceDisplay {...pricing} />
                                                        <AvailabilityToggle
                                                            available={isAvailable(product.is_available)}
                                                            loading={isPending(product.id, "food")}
                                                            onClick={() =>
                                                                handleChangeStatus(product.id, "food")
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            {hasVariants && (
                                                <div className="rest-menu-card__variants">
                                                    <div className="rest-menu-card__variants-head">
                                                        <span>نوع / سایز</span>
                                                        <span>قیمت</span>
                                                        <span>موجودی</span>
                                                    </div>

                                                    {product.options.map((item) => (
                                                        <div key={item.id} className="rest-menu-card__variant">
                                                            <span className="rest-menu-card__variant-name">
                                                                {item.name}
                                                            </span>
                                                            <div className="rest-menu-card__variant-row">
                                                                <PriceDisplay
                                                                    salePrice={Number(item.price_discount) || 0}
                                                                    basePrice={Number(item.price) || 0}
                                                                    discountPercent={item.discount_percent ? Number(item.discount_percent) : null}
                                                                />
                                                                <AvailabilityToggle
                                                                    compact
                                                                    available={isAvailable(item.is_available)}
                                                                    loading={isPending(item.id, "option")}
                                                                    onClick={() =>
                                                                        handleChangeStatus(item.id, "option")
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}

                                                    <div className="rest-menu-card__variant">
                                                        <span className="rest-menu-card__variant-name">
                                                            کل محصول
                                                        </span>
                                                        <div className="rest-menu-card__variant-row">
                                                            <span className="rest-menu-card__edit-hint">
                                                                همه گزینه‌ها
                                                            </span>
                                                            <AvailabilityToggle
                                                                compact
                                                                available={isAvailable(product.is_available)}
                                                                loading={isPending(product.id, "food")}
                                                                onClick={() =>
                                                                    handleChangeStatus(product.id, "food")
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </article>
                                    );
                                })}
                            </div>
                    )}
                </div>
            </section>
        </>
    );
};

export default ProductsMenu;
